import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Brand } from '../../interfaces/brands/brand';
import { DocumentBinary } from '../../interfaces/documents/document-binary';
import { CorporationRepositoryService } from '../../services/corporation/corporation-repository-service.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { BrandFormDialogComponent, BrandFormDialogData } from '../brand-form-dialog/brand-form-dialog.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'lib-brand-management',
  templateUrl: './brand-management.component.html',
  styleUrls: ['./brand-management.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BrandManagementComponent implements OnChanges {
  private readonly transloco = inject(TranslocoService);
  @Input() corporationID: string | null | undefined;

  brands: Brand[] = [];
  logoUrls: { [brandID: string]: string } = {};
  loading = false;

  constructor(
    private corporationRepo: CorporationRepositoryService,
    private dialog: MatDialog,
    private toastr: ToastrService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['corporationID'] && this.corporationID) {
      this.loadBrands();
    }
  }

  // ─── Data ──────────────────────────────────────────────────────────────────

  private loadBrands(): void {
    if (!this.corporationID) {
      this.brands = [];
      return;
    }

    this.loading = true;
    this.corporationRepo.getBrands(this.corporationID).subscribe({
      next: brands => {
        this.brands = brands ?? [];
        this.loading = false;
        this.loadLogos();
      },
      error: () => {
        this.brands = [];
        this.loading = false;
      }
    });
  }

  private loadLogos(): void {
    this.logoUrls = {};

    if (!this.corporationID) {
      return;
    }

    for (const brand of this.brands) {
      if (!brand.logoDocumentID) {
        continue;
      }

      this.corporationRepo.getBrandDocument(this.corporationID, brand.id).subscribe({
        next: doc => {
          if (doc?.base64) {
            this.logoUrls[brand.id] = `data:${this.mimeFor(doc)};base64,${doc.base64}`;
          }
        },
        error: () => { /* a missing logo just falls back to the colour swatch */ }
      });
    }
  }

  // ─── Add / edit / delete ─────────────────────────────────────────────────────

  openAdd(): void {
    if (!this.corporationID) {
      return;
    }

    this.openForm({ corporationID: this.corporationID, brand: null });
  }

  openEdit(brand: Brand): void {
    if (!this.corporationID) {
      return;
    }

    this.openForm({
      corporationID: this.corporationID,
      brand,
      currentLogoUrl: this.logoUrls[brand.id] ?? null
    });
  }

  private openForm(data: BrandFormDialogData): void {
    const dialogRef = this.dialog.open(BrandFormDialogComponent, {
      width: '480px',
      data
    });

    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.loadBrands();
      }
    });
  }

  confirmDelete(brand: Brand): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.transloco.translate('brandManagement.deleteBrandTitle'),
        confirmationPrompt: this.transloco.translate('brandManagement.deleteBrandPrompt', { name: brand.name }),
        negativeResponseLabel: this.transloco.translate('common.buttons.cancel'),
        positiveResponseLabel: this.transloco.translate('common.buttons.delete')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.corporationID) {
        this.corporationRepo.deleteBrand(this.corporationID, brand.id).subscribe({
          next: () => {
            this.toastr.success(this.transloco.translate('brandManagement.brandDeleted'));
            this.loadBrands();
          },
          error: () => this.toastr.error(this.transloco.translate('brandManagement.failedToDeleteBrand'))
        });
      }
    });
  }

  // ─── View helpers ─────────────────────────────────────────────────────────────

  swatchFor(brand: Brand): string | null {
    return brand.hexCode ? '#' + brand.hexCode : null;
  }

  private mimeFor(doc: DocumentBinary): string {
    const type = `${doc.documentTypeID}`.toUpperCase();
    if (type === 'JPG' || type === 'JPEG' || type === '9') {
      return 'image/jpeg';
    }
    return 'image/png';
  }
}
